// src/auth/create-password-page.tsx
//
// Route: /create-password. Flutter parity: create_password module (post-invite).
// A newly-invited provider follows the invite link, which carries their email +
// one-time access code as query params (`/create-password?email=…&code=…`). They
// verify the code (LoginByCode) and set their initial password (SetPassword) —
// the same two API steps as the reset flow, so the mechanics are the shared
// CodeToPasswordFlow. Difference vs reset: the email is fixed from the invite
// link (not editable) and there is no "resend" (the invite is issued by an admin).
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CodeToPasswordFlow } from './code-to-password-flow';

export function CreatePasswordPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  // Invite links carry the email + one-time access code as credentials. Two
  // possible carriers, checked in this order:
  //
  //   1. HASH FRAGMENT — /create-password#email=…&code=… . Fragments are never
  //      transmitted to the server: the browser strips everything from `#`
  //      onward before it even builds the HTTP request line. So a fragment
  //      never reaches server/CDN access logs, never rides a same-origin
  //      `Referer` header, and never shows up in server-side analytics — the
  //      client-side scrub below is fully sufficient for this carrier.
  //
  //   2. QUERY STRING (legacy / fallback) — /create-password?email=…&code=… .
  //      This is the carrier the FE CANNOT secure by itself: by the time this
  //      component's JS runs, the browser has ALREADY sent `?email=&code=` in
  //      the request line, so it is already in server/CDN logs and any
  //      same-origin `Referer` a linked-off-site resource might send. We still
  //      scrub it immediately (below) so the local tab/history doesn't retain
  //      it too, but that only limits the LOCAL blast radius — it does not
  //      undo the exposure that already happened server-side.
  //
  // BACKEND CHANGE REQUIRED (not made here — flagging only, no endpoint
  // invented): the invite-link generator — SendNotificationProviderHandlerEvent
  // / HostURLProvider — still emits `?email=&code=`. The durable fix is for it
  // to emit a `#email=&code=` fragment instead (or, better, an opaque one-time
  // invite token the backend resolves server-side, so no credential rides the
  // URL at all). This FE change only prefers the fragment when present and
  // keeps supporting the query string as a fallback; it cannot change what the
  // backend emits.
  const [invite] = useState(() => {
    const rawHash = window.location.hash;
    if (rawHash.length > 1) {
      const hashParams = new URLSearchParams(rawHash.slice(1));
      const hashEmail = hashParams.get('email');
      const hashCode = hashParams.get('code');
      if (hashEmail !== null || hashCode !== null) {
        return { email: hashEmail ?? '', code: hashCode ?? '' };
      }
    }
    return {
      email: params.get('email') ?? '',
      code: params.get('code') ?? '',
    };
  });

  useEffect(() => {
    // Scrub the hash fragment immediately if one was present, independent of
    // whether `invite` ended up reading from it (a malformed fragment without
    // email/code still shouldn't linger in the URL). replaceState so Back never
    // returns to the credential-bearing URL and so this doesn't create an extra
    // react-router history entry alongside the query-string scrub below.
    if (window.location.hash) {
      const url = new URL(window.location.href);
      url.hash = '';
      window.history.replaceState(window.history.state, '', url.toString());
    }
    // Scrub the query string too — the legacy carrier / fallback path. Kept
    // exactly as before so this doesn't regress the existing invite links still
    // being sent by the backend.
    if (params.get('email') !== null || params.get('code') !== null) {
      setParams({}, { replace: true });
    }
    // Run once on mount: `invite`/`params` are frozen at mount and `setParams`
    // is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { email, code } = invite;

  return (
    <CodeToPasswordFlow
      heading="Create your password"
      intro="Welcome to Scrips. Confirm the access code from your invite and set a password to finish setting up your account."
      initialEmail={email}
      initialCode={code}
      emailEditable={email === ''}
      showResend={false}
      submitLabel="Create password"
      successMessage="Your account is ready. Sign in with your new password."
      onDone={(successMessage) => navigate('/', { state: { flash: successMessage } })}
    />
  );
}
